// paywall — subscriptions through the Firebase Stripe extension.
//
// Two deliberate defaults:
//   · with no price id configured, every feature is unlocked. An app that gates
//     features before payments work looks broken rather than premium.
//   · entitlement is cached with a grace period, so a subscriber who opens the
//     app offline is not locked out of what they paid for.

import { firebaseApp, isConfigured } from '../auth/index.js';
import { prices, priceDisplay, paywallAvailable } from './prices.js';
import { state, bus } from '../../src/core/state.js';
import { prefs } from '../../src/core/storage.js';
import { toast } from '../../src/core/util.js';

const GRACE_MS = 7 * 24 * 60 * 60 * 1000;   // a week offline still counts

export function isPremium() {
  if (!paywallAvailable()) return true;      // paywall off ⇒ everything unlocked
  const cached = prefs.entitlement;
  if (!cached) return false;
  if (cached.premium && Date.now() - (cached.at ?? 0) < GRACE_MS) return true;
  return !!cached.premium && navigator.onLine === false;
}

export async function refreshEntitlement() {
  if (!paywallAvailable() || !state.user) return;
  const app = firebaseApp();
  if (!app) return;
  const { getFirestore, collection, getDocs } = await import(
    'https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js');
  const db = getFirestore(app);
  const subs = await getDocs(collection(db, 'customers', state.user.uid, 'subscriptions'));
  const active = subs.docs.some((d) => ['active', 'trialing'].includes(d.data().status));
  prefs.entitlement = { premium: active, at: Date.now() };
  bus.emit('entitlement', active);
}

export async function startCheckout(period = 'monthly') {
  if (!state.user) { toast('Sign in first'); return; }
  const price = prices[period];
  if (!price) return;

  const app = firebaseApp();
  const { getFirestore, collection, addDoc, onSnapshot } = await import(
    'https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js');
  const db = getFirestore(app);

  const ref = await addDoc(collection(db, 'customers', state.user.uid, 'checkout_sessions'), {
    price, success_url: location.origin, cancel_url: location.origin,
  });
  // The extension fills in the URL asynchronously, so watch the doc it made.
  onSnapshot(ref, (snap) => {
    const { url, error } = snap.data() ?? {};
    if (error) toast('Could not start checkout');
    if (url) location.assign(url);
  });
}

export default {
  id: 'paywall',

  boot() {
    if (!isConfigured() || !paywallAvailable()) return;
    bus.on('auth', (user) => { if (user) refreshEntitlement(); });
  },

  settingsRow() {
    if (!paywallAvailable()) return null;
    const row = document.createElement('label');
    row.className = 'row';
    row.innerHTML = `<span>Subscription</span>`;
    const btn = document.createElement('button');
    btn.className = 'btn';
    btn.textContent = isPremium() ? 'Active' : (priceDisplay.monthly || 'Upgrade');
    btn.addEventListener('click', () => { if (!isPremium()) startCheckout('monthly'); });
    row.appendChild(btn);
    return row;
  },
};
